import { httpClient } from '@/core/api/httpClient';
import type { Client, ClientsResponse, CreateClientPayload, UpdateClientPayload } from '../types';
import { clientsMock } from './clients.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

export interface ClientsQueryParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

export const clientsService = {
  getAll: async (params?: ClientsQueryParams, signal?: AbortSignal): Promise<ClientsResponse> => {
    if (USE_MOCK) {
      let items = [...clientsMock];

      if (params?.SearchTerm) {
        const searchLower = params.SearchTerm.toLowerCase();
        items = items.filter(
          (c) =>
            c.name.toLowerCase().includes(searchLower) ||
            c.taxId.toLowerCase().includes(searchLower),
        );
      }

      const page = params?.Page ?? 1;
      const pageSize = params?.PageSize ?? 20;
      const totalCount = items.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const start = (page - 1) * pageSize;

      return {
        items: items.slice(start, start + pageSize),
        page,
        pageSize,
        totalCount,
        totalPages,
      };
    }

    const { data } = await httpClient.get<ClientsResponse>(`${API_V1}/tenants/clients`, {
      params: {
        Page: params?.Page,
        PageSize: params?.PageSize,
        SearchTerm: params?.SearchTerm,
        SortBy: params?.SortBy,
        SortAscending: params?.SortAscending,
      },
      signal,
    });
    return data;
  },

  create: async (payload: CreateClientPayload): Promise<Client> => {
    if (USE_MOCK) {
      const newClient: Client = {
        id: `client-${Date.now()}`,
        name: payload.name,
        taxId: payload.taxId,
        isActive: true,
        createdAt: new Date().toISOString(),
      };
      clientsMock.push(newClient);
      return newClient;
    }

    const { data } = await httpClient.post<CreateClientPayload, Client>(
      `${API_V1}/tenants/clients`,
      payload,
    );
    return data;
  },

  update: async (clientId: string, payload: UpdateClientPayload): Promise<Client> => {
    if (USE_MOCK) {
      const index = clientsMock.findIndex((c) => c.id === clientId);
      if (index === -1) throw new Error('Client not found');
      const updated = { ...clientsMock[index], ...payload };
      clientsMock[index] = updated;
      return updated;
    }

    await httpClient.put<UpdateClientPayload, void>(
      `${API_V1}/tenants/clients/${clientId}`,
      payload,
    );
    return clientsService.getById(clientId);
  },

  getById: async (clientId: string, signal?: AbortSignal): Promise<Client> => {
    if (USE_MOCK) {
      const client = clientsMock.find((c) => c.id === clientId);
      if (!client) throw new Error('Client not found');
      return client;
    }

    const { data } = await httpClient.get<Client>(`${API_V1}/tenants/clients/${clientId}`, {
      signal,
    });
    return data;
  },

  delete: async (clientId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = clientsMock.findIndex((c) => c.id === clientId);
      if (index !== -1) clientsMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/tenants/clients/${clientId}`);
  },
};
